import { CONSENT_DETECTION_TIMEOUT, CONSENT_DETECTION_INTERVAL, REASONS } from '../../config.js';
import { debug } from './debug.js';
import { CONSENT } from '@sklik/cmp2-common';
import { COOKIE } from '@iva/utils';

export const [RUN, DO_NOT_RUN, UNCERTAIN] = [1, 2, 3];

/**
 * Read data from window and decides whether to run callback,
 * never run callback or if it cannot be decided yet.
 * @returns {Object} Object with result, reason and consent string
 */
export const getShouldRunFromWindow = () => {
  const cmpone = COOKIE.getCookie(CONSENT.CONSENT_PURPOSE_ONE_COOKIE_NAME);
  const consent = CONSENT.getConsentFromCookieList();
  const consentString = consent?.consentString || null;

  const buildResult = (result, reason) => {
    return { result, reason, consent: consentString };
  };

  if (cmpone === '1') {
    return buildResult(RUN, REASONS.CMPONE);
  }
  if (cmpone === '0') {
    return buildResult(DO_NOT_RUN, REASONS.CMPONE);
  }

  if (window.rc && !window.rc.internal) {
    if (window.rc.consent === 1) {
      return buildResult(RUN, REASONS.RCCONSENT);
    }
    if (window.rc.consent === 0) {
      return buildResult(DO_NOT_RUN, REASONS.RCCONSENT);
    }
  }

  if (consent) {
    return buildResult(consent.purposeOne ? RUN : DO_NOT_RUN, REASONS.IABCONSENT);
  }

  return buildResult(UNCERTAIN, REASONS.NO_CONSENT);
};

/**
 * Wait for consent to store SID cookie. After timeout return that there is no.
 * @param {*} callback function with shouldRun argument
 * @param {number} customTimeout custom timeout of consent waiting in ms
 * @returns undefined
 */
export const detectShouldRun = (callback, consentTimeout) => {
  let shouldRun = getShouldRunFromWindow();
  // Run immediately
  if (shouldRun.result !== UNCERTAIN) {
    callback(shouldRun);
    return;
  }

  let interval = null;
  let iterationCount = 0;

  const consentTimeoutOk = typeof consentTimeout === 'number' && consentTimeout >= 0;
  const maxIterations = Math.ceil(
    consentTimeoutOk
      ? consentTimeout / CONSENT_DETECTION_INTERVAL
      : CONSENT_DETECTION_TIMEOUT / CONSENT_DETECTION_INTERVAL
  );

  /**
   * Clear interval and event listener, then execute the callback
   */
  const clearAndRun = (_shouldRun) => {
    clearInterval(interval);
    interval = null;
    callback(_shouldRun);
  };

  const periodicalCheck = () => {
    shouldRun = getShouldRunFromWindow();
    // Should run
    if (shouldRun.result !== UNCERTAIN) {
      clearAndRun(shouldRun);
      return;
    }

    // Keep checking
    iterationCount++;

    // CONSENT_DETECTION_INTERVAL have elapsed, stop checking
    if (iterationCount >= maxIterations) {
      clearAndRun({ result: DO_NOT_RUN, reason: REASONS.TIMEOUT, consent: shouldRun.consent });
      debug('giving up');
      return;
    }
    debug("unsure whether to run, I'll keep trying");
  };

  // Check periodically
  interval = setInterval(periodicalCheck, CONSENT_DETECTION_INTERVAL);

  // wait for RC event
  const handleRCEvent = (e) => {
    if (e.detail && e.detail.consent) {
      window.removeEventListener('szn:rc:setconsentvalue', handleRCEvent);
      clearAndRun({ result: RUN, reason: REASONS.RCCONSENT, consent: getShouldRunFromWindow().consent });
    }
  };

  window.addEventListener('szn:rc:setconsentvalue', handleRCEvent);
};
